import React, { useEffect, useState } from 'react';
import { UserPlus, UserMinus, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/i18n';
import { toast } from 'sonner';

/**
 * Follow/unfollow another user. Also displays follower count.
 * Props: userId (required), onCountChange?(newCount)
 */
export default function FollowButton({ userId, initialCount = null, initialIsFollowing = null, onCountChange }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [count, setCount] = useState(initialCount);
  const [isFollowing, setIsFollowing] = useState(initialIsFollowing);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialCount === null || initialIsFollowing === null) {
      api.get(`/users/${userId}/followers`)
        .then(({ data }) => { setCount(data.followers_count); setIsFollowing(data.is_following); })
        .catch(() => {});
    }
  }, [userId, initialCount, initialIsFollowing]);

  if (!user || user === false || user.id === userId) return null;

  const toggle = async () => {
    setLoading(true);
    try {
      if (isFollowing) {
        await api.delete(`/users/${userId}/follow`);
        setIsFollowing(false);
        const c = Math.max(0, (count || 0) - 1);
        setCount(c);
        onCountChange?.(c);
        toast.success(t('follow.unfollowed'));
      } else {
        await api.post(`/users/${userId}/follow`);
        setIsFollowing(true);
        const c = (count || 0) + 1;
        setCount(c);
        onCountChange?.(c);
        toast.success(t('follow.followed'));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t('common.error'));
    } finally { setLoading(false); }
  };

  return (
    <button
      type="button"
      data-testid={`follow-btn-${userId}`}
      onClick={toggle}
      disabled={loading}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition disabled:opacity-50 ${
        isFollowing
          ? 'bg-white/10 text-white/70 hover:bg-red-500/10 hover:text-red-300 border border-white/10'
          : 'bg-[#FFD479] text-black hover:bg-[#FFD479]/90'
      }`}
    >
      {loading ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : isFollowing ? (
        <><UserMinus className="w-3.5 h-3.5" /> {t('follow.following')}</>
      ) : (
        <><UserPlus className="w-3.5 h-3.5" /> {t('follow.follow')}</>
      )}
    </button>
  );
}
